/**
 * Класс placeHTML.
 *
 * Основные возможности:
 *  - Размещение переданного HTML (или загруженного по URL) в заданном месте (селектор/DOM-элемент/stack).
 *  - Поддержка типов: `html`, `modal`, `toast`, `alert` (можно расширять).
 *  - Опции загрузки: GET/POST, динамические параметры, обработка ошибок.
 *  - Отложенная вставка (delayed).
 *  - Автоматическое закрытие (autoClose).
 *  - Кнопка закрытия (hasClose).
 *  - При type=toast/alert, если placeIn=stack, создаются stack-контейнеры.
 *  - События (колбэки): onInit, beforeLoad, beforePlace, afterPlace, onHide, onDelete.
 *  - Дебаг-режим (debug=true) — вывод логов в консоль.
 *  - Удаление DOM-элемента после закрытия (deleteAfterClose=true).
 *  - Для modal — загрузка по URL + инициализация Bootstrap.Modal.
 *  - Для toast/alert — если нет url, генерируем верстку самостоятельно.
 *  - Поддержка массива кнопок (buttons) для toast/alert.
 *  - Авто-создание stack-контейнеров.
 *  - Связываем объект класса с DOM-элементом (instance).
 *  - Явное указание caller для отображения спиннера.
 *
 * v.0.2.250414 - текущая версия
 * v.0.2.250505 - добавлен backdrop
 * v.0.3.250808 - поправил вызов коллбеков типа afterPlace
 *
 * @class placeHTML
 */

class placeHTML {
	/**
	 * Статические опции по умолчанию.
	 * @property {string|Element} placeIn
	 * @property {string|boolean} url
	 * @property {string} method
	 * @property {object} fetchParams
	 * @property {string} type
	 * @property {string|boolean} html
	 * @property {string} position
	 * @property {boolean} debug
	 * @property {number|boolean} delayed
	 * @property {number|boolean} autoClose
	 * @property {boolean} deleteAfterClose
	 * @property {boolean} hasClose
	 * @property {string} class
	 * @property {object} params
	 * @property {string} paramsType
	 * @property {string} stackId
	 * @property {string} stackPosition
	 * @property {string} title
	 * @property {string} body
	 * @property {Array} buttons
	 * @property {function|null} onInit
	 * @property {function|null} beforeLoad
	 * @property {function|null} beforePlace
	 * @property {function|null} afterPlace
	 * @property {function|null} onHide
	 * @property {function|null} onDelete
	 * @property {string} placeHTMLclass
	 * @property {string} dataAttrName
	 * @property {Element|null} caller
	 * @property {boolean|string} spinner
	 * @property {string} loadingClass
	 */
	static defaultOptions = {
		placeIn: 'body',
		url: false,
		method: 'GET',
		fetchParams: {},
		type: 'html',
		html: false,
		position: 'beforeEnd',
		debug: false,
		delayed: false,
		autoClose: false,
		deleteAfterClose: true,
		hasClose: false,
		backdrop: false,
		backdropClassMain: '_backdrop',
		backdropClass: '',
		backdropStatic: false,
		class: '',
		params: {},
		paramsType: 'default',
		stackId: '',
		stackPosition: 'top center',
		title: '',
		body: '',
		buttons: [],
		onInit: null,
		beforeLoad: null,
		beforePlace: null,
		afterPlace: null,
		onHide: null,
		onDelete: null,
		placeHTMLclass: '_dynamic',
		dataAttrName: 'data-placehtml',
		caller: null,
		spinner: false,
		loadingClass: '_loading'
	}

	constructor(userOptions = {}) {
		// Флаг для защиты от повторного закрытия
		this._closing = false

		this._mergeOptions(userOptions)
		this._prepareCallbacksFromStrings()
		this._setupCallbacks()
		this._setupCaller()

		this._debug('init', 'Инициализация placeHTML, итоговые опции:', this.options)
		if (typeof this.onClassInit === 'function') {
			this.onClassInit(this)
		}

		if (this.options.delayed && Number(this.options.delayed) > 0) {
			setTimeout(() => this.process(), this.options.delayed)
		} else {
			this.process()
		}
	}

	_mergeOptions(userOptions) {
		const typeDefaultOptions = {
			modal: {
				placeIn: 'body',
				backdrop: true,
			},

			toast: {
				placeIn: 'stack',
				stackPosition: 'top center',
				backdropClass: '_backdrop-toast'
			},

			alert: {
				placeIn: 'stack',
				stackPosition: 'top center',
				backdropClass: '_backdrop-alert'
			},

			html_place: {
				position: 'beforeEnd',
			}
		}

		let merged = { ...placeHTML.defaultOptions }
		if (userOptions.type && typeDefaultOptions[userOptions.type]) {
			merged = { ...merged, ...typeDefaultOptions[userOptions.type] }
		}

		this.options = { ...merged, ...userOptions }
		if(this.options.debug) console.log(this.options)
	}

	_setupCallbacks() {
		this.onClassInit = this.options.onInit
		this.beforeClassLoad = this.options.beforeLoad
		this.beforeClassPlace = this.options.beforePlace
		this.afterClassPlace = this.options.afterPlace
		this.onClassHide = this.options.onHide
		this.onClassDelete = this.options.onDelete
	}

	_setupCaller() {
		if (this.options.caller) {
			this.originalCallerHTML = (this.options.spinner && this.options.placeIn !== this.options.caller)
				? this.options.caller.innerHTML
				: null
		} else {
			this.originalCallerHTML = null
		}
	}

	_prepareCallbacksFromStrings() {
		const cbNames = ['onInit', 'beforeLoad', 'beforePlace', 'afterPlace', 'onHide', 'onDelete'];

		cbNames.forEach(name => {
			const cb = this.options[name];
			if (typeof cb === 'string') {
				try {
					// Создаем функцию с аргументом instance
					this.options[name] = new Function('instance', cb);
				} catch (e) {
					this._debug('error', `Ошибка парсинга коллбэка ${name}:`, e);
					this.options[name] = null;
				}
			}
		});
	}

	process() {
		if (this.options.caller) {
			this.options.caller.classList.add(this.options.loadingClass)
			if (this.options.spinner) {
				const spinnerHTML = this.options.spinner === true
					? '<div class="spinner-border" role="status"></div>'
					: (typeof this.options.spinner === 'string' ? this.options.spinner : '')
				if (spinnerHTML) {
					this.options.caller.innerHTML = spinnerHTML
				}
			}
		}

		if (this.options.url) {
			this._fetchContent()
				.then(content => {
					this._clearSpinner()
					this._placeContent(content)
					this._finalizeCaller()
				})
				.catch(err => {
					this._debug('error', 'Ошибка загрузки:', err)
					this._clearSpinner()
					this._finalizeCaller()
				})
		} else {
			let finalContent = this.options.html
			if (!finalContent) {
				if (this.options.type === 'toast') {
					finalContent = this._createToastHTML()
				} else if (this.options.type === 'alert') {
					finalContent = this._createAlertHTML()
				}
			}
			this._clearSpinner()
			this._placeContent(finalContent)
			this._finalizeCaller()
		}
	}

	_clearSpinner() {
		if (this.options.caller && this.options.spinner) {
			if (this.options.placeIn === this.options.caller) {
				this.options.caller.innerHTML = ''
			}
		}
	}

	_finalizeCaller() {
		if (this.options.caller) {
			this.options.caller.classList.remove(this.options.loadingClass)
			if (this.options.spinner && this.originalCallerHTML !== null && this.options.placeIn !== this.options.caller) {
				this.options.caller.innerHTML = this.originalCallerHTML
			}
		}
	}

	_fetchContent() {
		return new Promise((resolve, reject) => {
			if (typeof this.beforeClassLoad === 'function') {
				this.beforeClassLoad(this)
			}

			let url = this.options.url
			let fetchOptions = {
				method: this.options.method,
				...this.options.fetchParams
			}
			const params = this.options.params || {}

			if (Object.keys(params).length > 0) {
				if (this.options.method.toUpperCase() === 'GET') {
					const queryString = new URLSearchParams(params).toString()
					url += (url.includes('?') ? '&' : '?') + queryString
				} else {
					if (this.options.paramsType === 'json') {
						fetchOptions.headers = {
							...fetchOptions.headers,
							'Content-Type': 'application/json'
						}
						fetchOptions.body = JSON.stringify(params)
					} else {
						fetchOptions.headers = {
							...fetchOptions.headers,
							'Content-Type': 'application/x-www-form-urlencoded'
						}
						fetchOptions.body = new URLSearchParams(params)
					}
				}
			}

			this._debug('info', `URL запрос: ${url}, метод: ${this.options.method}`, fetchOptions)
			fetch(url, fetchOptions)
				.then(response => {
					if (!response.ok) {
						throw new Error(`HTTP error: ${response.status}`)
					}
					return response.text()
				})
				.then(html => {
					resolve(html)
				})
				.catch(err => {
					reject(err)
				})
		})
	}

	_placeContent(content) {
		if (!content) {
			this._debug('warn', 'Контент для вставки отсутствует')
			return
		}

		if (typeof this.beforeClassPlace === 'function') {
			this.beforeClassPlace(this)
		}

		this.element = this._resolvePlaceIn(this.options.placeIn)
		if (!this.element) {
			this._debug('error', `Элемент для вставки не найден: ${this.options.placeIn}`)
			return
		}

		const tmpEl = document.createElement('div')
		tmpEl.innerHTML = content.trim()

		const domToInsert = (tmpEl.childNodes.length === 1)
			? tmpEl.firstChild
			: Array.from(tmpEl.childNodes)

		if (this.options.class && domToInsert instanceof Element && !Array.isArray(domToInsert)) {
			domToInsert.classList.add(...this.options.class.split(' ').filter(Boolean))
		}

		switch (this.options.position) {
			case 'innerHTML':
				this.element.innerHTML = content
				this.createdEl = this.element.firstChild
				break
			case 'outerHTML':
				this.element.outerHTML = content
				this.createdEl = null
				break
			case 'beforeBegin':
				if (Array.isArray(domToInsert)) {
					domToInsert.forEach(node => {
						this.element.insertAdjacentElement('beforebegin', node)
					})
					this.createdEl = domToInsert[domToInsert.length - 1]
				} else {
					this.element.insertAdjacentElement('beforebegin', domToInsert)
					this.createdEl = domToInsert
				}
				break
			case 'afterBegin':
				if (Array.isArray(domToInsert)) {
					domToInsert.reverse().forEach(node => {
						this.element.insertAdjacentElement('afterbegin', node)
					})
					this.createdEl = domToInsert[0]
				} else {
					this.element.insertAdjacentElement('afterbegin', domToInsert)
					this.createdEl = domToInsert
				}
				break
			case 'afterEnd':
				if (Array.isArray(domToInsert)) {
					domToInsert.forEach(node => {
						this.element.insertAdjacentElement('afterend', node)
					})
					this.createdEl = domToInsert[domToInsert.length - 1]
				} else {
					this.element.insertAdjacentElement('afterend', domToInsert)
					this.createdEl = domToInsert
				}
				break
			case 'beforeEnd':
			default:
				if (Array.isArray(domToInsert)) {
					domToInsert.forEach(node => {
						this.element.appendChild(node)
					})
					this.createdEl = domToInsert[domToInsert.length - 1]
				} else {
					this.element.insertAdjacentElement('beforeend', domToInsert)
					this.createdEl = domToInsert
				}
				break
		}

		// Привязываем instance
		if (this.createdEl && this.createdEl instanceof Element) {
			this.createdEl.instance = this
		} else if (Array.isArray(this.createdEl)) {
			this.createdEl.forEach(el => {
				if (el instanceof Element) {
					el.instance = this
				}
			})
		}

		// Выполняем inline-скрипты
		this._executeScripts(content)

		// Включаем "show"/"fade" анимации для всех типов, если хотим единый подход.
		// Но ниже – используем стандартные механизмы bootstrap, где нужно.

		// Modal
		if (this.options.type === 'modal' && this.createdEl) {
			let modalEl = null
			const bsBackdrop = this.options.backdrop
				? (this.options.backdropStatic ? 'static' : true)
				: false
			const bsKeyboard = bsBackdrop != 'static'

			if (this.createdEl.classList && this.createdEl.classList.contains('modal')) {
				modalEl = this.createdEl
			} else {
				modalEl = this.createdEl.querySelector('.modal')
			}
			if (modalEl) {
				modalEl.classList.add(this.options.placeHTMLclass)
				const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, {
					backdrop: bsBackdrop,
					keyboard: bsKeyboard
				})
				modalInstance.show()

				modalEl.addEventListener('hidden.bs.modal', () => {
					this._debug('info', 'Modal скрыт')
					document.querySelector('body').focus()
					if (typeof this.onClassHide === 'function') {
						this.onClassHide(this, modalEl)
					}
					if (this.options.deleteAfterClose) {
						modalEl.remove()
						if (typeof this.onClassDelete === 'function') {
							this.onClassDelete(this, modalEl)
						}
					}
					this._closingHandler()
				})
			}
		}

		// Toast
		if (this.options.type === 'toast' && this.createdEl) {
			let toastEl = null
			if (this.createdEl.classList && this.createdEl.classList.contains('toast')) {
				toastEl = this.createdEl
			} else {
				toastEl = this.createdEl.querySelector('.toast')
			}
			if (toastEl) {
				this._createBackdrop()
				// Включим анимацию, чтобы при hide() был плавный эффект
				const toastInstance = bootstrap.Toast.getOrCreateInstance(toastEl, {
					autohide: false,
					animation: true
				})

				// Двойной rAF для плавного "show"
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						toastInstance.show()
					})
				})

				if (this.options.autoClose) {
					setTimeout(() => {
						toastInstance.hide()
					}, this.options.autoClose)
				}

				toastEl.addEventListener('hidden.bs.toast', () => {
					this._debug('info', 'Toast скрыт')
					this._closingHandler()
					if (typeof this.onClassHide === 'function') {
						this.onClassHide(this, toastEl)
					}
					if (this.options.deleteAfterClose) {
						toastEl.remove()
						if (typeof this.onClassDelete === 'function') {
							this.onClassDelete(this, toastEl)
						}
					}
				})
			}
		}

		// Alert
		if (this.options.type === 'alert' && this.createdEl) {
			let alertEl = null
			if (this.createdEl.classList && this.createdEl.classList.contains('alert')) {
				alertEl = this.createdEl
			} else {
				alertEl = this.createdEl.querySelector('.alert')
			}
			if (alertEl) {
				this._createBackdrop()
				// Добавим fade, уберем show, потом двойной rAF => show (для анимации появления)
				alertEl.classList.add('fade')
				alertEl.classList.remove('show')
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						alertEl.classList.add('show')
					})
				})

				if (this.options.autoClose) {
					setTimeout(() => {
						this._closeAlert(alertEl)
					}, this.options.autoClose)
				}

				// При закрытии (bootstrap close) отработает fade out
				alertEl.addEventListener('close.bs.alert', () => {
					this._closingHandler()
					if (typeof this.onClassHide === 'function') {
						this.onClassHide(this, alertEl)
					}
				})
				alertEl.addEventListener('closed.bs.alert', () => {
					this._debug('info', 'Alert скрыт (closed.bs.alert)')
					if (this.options.deleteAfterClose) {
						if (typeof this.onClassDelete === 'function') {
							this.onClassDelete(this, alertEl)
						}
					}
				})
			}
		}

		if (typeof this.afterClassPlace === 'function') {
			this.afterClassPlace(this)
		}
	}



	// Выполняем inline-скрипты для загруженного контента, а не для всего body
	_executeScripts(html) {
		// Создаем временный элемент для обработки скриптов
		const tempContainer = document.createElement('div')
		tempContainer.innerHTML = html.trim()

		// Находим все теги <script> внутри контейнера
		const scripts = tempContainer.querySelectorAll('script')

		scripts.forEach(script => {
			const newScript = document.createElement('script')

			Array.from(script.attributes).forEach(attr => {
				newScript.setAttribute(attr.name, attr.value)
			})

			newScript.textContent = script.textContent
			document.body.appendChild(newScript)
			newScript.remove()
		})

		// Удаляем скрипты из контейнера, чтобы не дублировать контент
		scripts.forEach(script => script.remove())
	}



	_resolvePlaceIn(placeIn) {
		// Если placeIn=stack для toast/alert – создаем или используем stack
		if (placeIn === 'stack' && (this.options.type === 'toast' || this.options.type === 'alert')) {
			return this._getOrCreateStackContainer()
		}

		if (typeof placeIn === 'string') {
			if (placeIn === 'body') {
				return document.body
			}
			const foundEl = document.querySelector(placeIn)
			if (!foundEl) {
				this._debug('warn', `Селектор "${placeIn}" не найден, используем <body> вместо него.`)
				return document.body
			}
			return foundEl
		} else if (placeIn instanceof Element) {
			return placeIn
		}
		return document.body
	}

	_getOrCreateStackContainer() {
		const [vertical, horizontal] = this._parseStackPosition(this.options.stackPosition)
		// Если stackId не задан — генерируем его
		const stackId = this.options.stackId || `stack-${this.options.type}-${vertical}-${horizontal}`
		// Проверяем наличие элемента с таким ID
		let stackContainer = document.getElementById(stackId)
		if (stackContainer) return stackContainer

		// Сохраняем ID в опции, чтобы в дальнейшем он использовался
		this.options.stackId = stackId

		// Создаем новый контейнер
		stackContainer = document.createElement('div')
		stackContainer.id = stackId
		stackContainer.classList.add(`stack_pos-v-${vertical}`, `stack_pos-h-${horizontal}`)
		stackContainer.classList.add('notices_stack', `stack-${this.options.type}s`)

		document.body.appendChild(stackContainer)
		return stackContainer
	}


	_parseStackPosition(posString) {
		const vSet = new Set(['top', 'bottom'])
		const hSet = new Set(['left', 'right'])

		const parts = posString.trim().toLowerCase().split(/\s+/)
		let vertical = 'center'
		let horizontal = 'center'

		parts.forEach(p => {
			if (vSet.has(p)) vertical = p
			else if (hSet.has(p)) horizontal = p
		})

		return [vertical, horizontal]
	}


	_createToastHTML() {
		const { title, body, hasClose, buttons } = this.options
		let btnsHtml = ''
		if (Array.isArray(buttons) && buttons.length) {
			btnsHtml = `<div class="btns__wrap">`
			buttons.forEach(btnObj => {
				const btnTag = btnObj.tag || 'button'
				let otherAttrs = ''
				Object.keys(btnObj).forEach(key => {
					if (!['tag', 'title', 'class', 'onClick'].includes(key)) {
						otherAttrs += ` ${key}="${btnObj[key]}"`
					}
				})
				btnsHtml += `\n          <${btnTag} class="${btnObj.class || ''}"${otherAttrs}>\n            ${btnObj.title || ''}\n          </${btnTag}>\n        `
			})
			btnsHtml += `</div>`
		}

		const closeBtnHtml = hasClose
			? `<button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>`
			: ''

		const htmlHead = title
			? `<div class="toast-header">\n           <div class="toast-title">${title}</div>\n         </div>`
			: ''

		const htmlBody = (body || btnsHtml)
			? `<div class="toast-body">${body}${btnsHtml}</div>`
			: ''

		return `<div class="toast ${this.options.placeHTMLclass}" role="alert" aria-live="assertive" aria-atomic="true">\n      ${closeBtnHtml}${htmlHead}${htmlBody}\n    </div>`
	}

	_createAlertHTML() {
		const { title, body, hasClose, buttons } = this.options
		let btnsHtml = ''
		if (Array.isArray(buttons) && buttons.length) {
			btnsHtml = '<div class="btns__wrap">'
			buttons.forEach(btnObj => {
				const btnTag = btnObj.tag || 'button'
				let otherAttrs = ''
				Object.keys(btnObj).forEach(key => {
					if (!['tag', 'title', 'class', 'onClick'].includes(key)) {
						otherAttrs += ` ${key}="${btnObj[key]}"`
					}
				})
				btnsHtml += `\n          <${btnTag} class="${btnObj.class || ''}"${otherAttrs}>\n            ${btnObj.title || ''}\n          </${btnTag}>\n        `
			})
			btnsHtml += '</div>'
		}

		let alertClass = (this.options.class !== '') ? this.options.class : 'alert-warning'

		const closeBtnHtml = hasClose
			? `<button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>`
			: ''

		const alertTitle = title ? `<h4 class="alert-heading">${title}</h4>` : ''

		// alert + fade (для анимации), без show — добавим позже
		return `\n      <div class="alert ${this.options.placeHTMLclass} ${alertClass} alert-dismissible fade" role="alert">\n        ${closeBtnHtml}\n        ${alertTitle}\n        <div>${body}</div>\n        ${btnsHtml}\n      </div>\n    `
	}

	_closeAlert(alertEl) {
		const alertInstance = bootstrap.Alert.getOrCreateInstance(alertEl)
		alertInstance.close()
		this._closingHandler()
	}


	_closeIfPossible() {
		if (this._closing) return
		this._closing = true

		if (!this.createdEl) return

		if (this.options.type === 'toast') {
			let toastEl = (this.createdEl.classList && this.createdEl.classList.contains('toast'))
				? this.createdEl
				: this.createdEl.querySelector('.toast')
			if (toastEl) {
				bootstrap.Toast.getOrCreateInstance(toastEl).hide()
			}

		} else if (this.options.type === 'alert') {
			let alertEl = (this.createdEl.classList && this.createdEl.classList.contains('alert'))
				? this.createdEl
				: this.createdEl.querySelector('.alert')
			if (alertEl) {
				bootstrap.Alert.getOrCreateInstance(alertEl).close()
			}

		} else if (this.options.type === 'modal') {
			let modalEl = this.createdEl.querySelector('.modal')
			if (!modalEl && this.createdEl.classList.contains('modal')) {
				modalEl = this.createdEl
			}
			if (modalEl) {
				bootstrap.Modal.getOrCreateInstance(modalEl).hide()
			}
		} else if (this.options.type === 'html') {
			this.createdEl.remove()
		}
	}


	_closingHandler() {
		this._removeBackdrop()
	}


	static ask(userOptions = {}) {
		return new Promise((resolve, reject) => {
			const inst = new placeHTML({
				...userOptions,
				afterPlace(instance) {
					if (typeof userOptions.afterPlace === 'function') {
						userOptions.afterPlace(instance)
					}

					if (Array.isArray(instance.options.buttons)) {
						instance.options.buttons.forEach((btnObj) => {
							const btnTag = btnObj.tag || 'button'
							const possibleButtons = instance.createdEl.querySelectorAll(btnTag)
							possibleButtons.forEach((el) => {
								if (el.textContent.trim() === (btnObj.title || '').trim()) {
									el.addEventListener('click', (event) => {
										if (instance._closing) return

										if (typeof btnObj.onClick === 'function') {
											btnObj.onClick(instance, event, resolve, reject)
										} else {
											if (typeof userOptions.onConfirm === 'function' && btnObj.title.toLowerCase().includes('ok')) {
												userOptions.onConfirm(instance, event, resolve, reject)
											} else if (typeof userOptions.onCancel === 'function' && btnObj.title.toLowerCase().includes('cancel')) {
												userOptions.onCancel(instance, event, resolve, reject)
											} else {
												resolve(null)
												instance._closeIfPossible()
											}
										}
									})
								}
							})
						})
					}
				},
				onHide(instance, domEl) {
					if (typeof userOptions.onHide === 'function') {
						userOptions.onHide(instance, domEl)
					}
					if (!instance._closing) {
						if (typeof userOptions.onCancel === 'function') {
							userOptions.onCancel(instance, null, resolve, reject)
						} else {
							reject('cancel')
						}
					}
				}
			})
		})
	}


	_createBackdrop() {
		if (this.options.backdrop != true) return

		this.backdropEl = document.createElement('div')
		this.backdropEl.classList.add(this.options.backdropClassMain, 'fade', this.options.backdropClass)
		document.body.appendChild(this.backdropEl)
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.backdropEl.classList.add('show')
			})
		})

		// Esc‑handler
		if (this.options.backdrop && this.options.backdropStatic == false) {
			const escHandler = e => {if (e.key === 'Escape') this._closeIfPossible()}
			document.addEventListener('keydown', escHandler)

			// клик по фону
			const onClick = () => {this._closeIfPossible()}
			this.backdropEl.addEventListener('click', onClick)
		}
	}

	// удаляет фон и отписывает event‑listeners
	_removeBackdrop() {
		if(typeof this.backdropEl != 'undefined') {
			this.backdropEl.classList.remove('show')

			setTimeout(() => {
				if (this.backdropEl) {
					this.backdropEl.remove()
					delete this.backdropEl
				}
				if (this._escHandler) {
					document.removeEventListener('keydown', this._escHandler)
					delete this._escHandler
				}
			}, 500)
		}
	}

	_debug(level, ...args) {
		if (!this.options.debug) return
		switch (level) {
			case 'info':
				console.info('[placeHTML]', ...args)
				break
			case 'warn':
				console.warn('[placeHTML]', ...args)
				break
			case 'error':
				console.error('[placeHTML]', ...args)
				break
			default:
				console.log('[placeHTML]', ...args)
		}
	}
}

const dataAttr = placeHTML.defaultOptions.dataAttrName

document.querySelectorAll(`[${dataAttr}]`).forEach(el => {
	const configStr = el.getAttribute(dataAttr)
	if (configStr) {
		try {
			const config = new Function('return ' + configStr)()
			config.placeIn = el
			config.type = 'html_place'
			config.caller = el
			new placeHTML(config)
		} catch (e) {
			console.error('placeHTML: ошибка парсинга data-атрибута:', e)
		}
	}
})
